import React, { useCallback, useMemo, useState } from 'react'
import Publisher, { IPB, PublishType } from 'Components/publisher/publisher'
import useLoginCheckCallback from 'Components/hooks/use-login-check-callback'
import Toast from 'Components/toast-messager'
import Dialog from 'Components/dialog'
import classNames from 'classnames'
import dialogStyle from 'Components/dialog.module.scss'

const publishPicture = require('static/publish-picture.svg')
const publishQa = require('static/publish-qa.svg')
// const publishScore = require('static/publish-score.svg')
const publishVideo = require('static/publish-video.svg')
const publishLong = require('static/publish-long.svg')

type IOperationType = PublishType | 'score' | 'long'

interface IOperation {
  title: string
  type: IOperationType
  des: string
  src: string
  url?: string
}

const operations: IOperation[] = [
  {
    title: '发视频',
    type: 'video',
    des: '视频分享爱车/用车体验/提车作业/自驾游',
    src: publishVideo,
  },
  {
    title: '发图文',
    type: 'picture',
    des: '图文分享爱车/用车体验/提车作业/自驾游',
    src: publishPicture,
  },
  {
    title: '提问',
    type: 'qa',
    des: '选买/用车问题，专业技师24小时在线解答',
    src: publishQa,
  },
]

// const score: IOperation = {
//   title: '懂车分',
//   type: 'score',
//   des: '发表爱车口碑，为车友提供选车参考',
//   src: publishScore,
//   url: "https://mp.dcdapp.com/profile_v2/publish/car-score/recommend"
// }
const score: IOperation = {
  title: '发长帖',
  type: 'long',
  des: '交流买车/用车经验、分享有车生活',
  src: publishLong,
  url: 'https://mp.dcdapp.com/ugc/publish/#/post',
}

export function usePublish() {
  const [isShowPublish, setIsShowPublish] = useState(false)
  const [operation, setOperation] = useState('picture')

  const handlePublish = useLoginCheckCallback(
    useCallback((operation?: IOperation) => {
      if (operation?.url) {
        window.open(operation.url)
        return
      }
      setOperation(operation?.type || 'picture')
      setIsShowPublish(true)
    }, []),
    useMemo(
      () => ({
        enter_method: 'publish',
      }),
      []
    )
  )

  const publishSuccess = useCallback(async (props: IPB) => {
    Toast.show({
      text: '发布成功',
    })
    setIsShowPublish(false)
    setTimeout(() => {
      window.open(`/ugc/article/${props.gid}`)
    }, 3000)
  }, [])

  return {
    isShowPublish,
    setIsShowPublish,
    operation,
    handlePublish,
    publishSuccess,
  }
}

interface IProps {
  hasScore?: boolean
  seriesId?: string
  seriesName?: string
  topicId?: string
  topicName?: string
  onClick?(text: string): void
}

interface IPublishOperationProps extends IProps {
  className?: string
}

// 平铺 + sm 相应式发布操作按钮
export const PublishOperation = ({
  hasScore = true,
  seriesId,
  seriesName,
  topicId,
  topicName,
  className,
  onClick,
}: IPublishOperationProps) => {
  const {
    isShowPublish,
    setIsShowPublish,
    operation,
    handlePublish,
    publishSuccess,
  } = usePublish()

  return (
    <>
      <div
        className={classNames(
          'tw-grid tw-gap-6 md:tw-grid-cols-2',
          hasScore ? 'tw-grid-cols-4' : 'tw-grid-cols-3',
          className
        )}
      >
        {[...operations, ...(hasScore ? [score] : [])].map((item, index) => {
          return (
            <div
              className={classNames(
                'tw-h-66 tw-px-16 tw-py-12 tw-flex tw-justify-between tw-items-center tw-rounded-2 tw-bg-white tw-cursor-pointer publish',
                {
                  'md:tw-col-span-2':
                    !hasScore && index === operations.length - 1,
                }
              )}
              key={index}
              onClick={() => {
                if (onClick) onClick(item.title)
                handlePublish(item)
              }}
            >
              <div className="tw-z-10 tw-overflow-hidden">
                <p className="tw-text-14 md:tw-text-16 tw-font-medium">
                  {item.title}
                </p>
                <p
                  className="tw-text-12 tw-text-gray-500 des line-1"
                  title={item.des}
                >
                  {item.des}
                </p>
              </div>
              <img
                className="tw-w-32 tw-h-32 tw-flex-none tw-z-10"
                src={item.src}
                height={32}
                width={32}
              />
            </div>
          )
        })}
      </div>
      <Dialog
        className={dialogStyle['space-medium']}
        headerClass={dialogStyle['space-medium']}
        visible={isShowPublish}
        onClose={() => setIsShowPublish(false)}
      >
        {isShowPublish ? (
          <Publisher
            defaultCommunity={
              seriesId
                ? {
                    series_id: seriesId,
                    series_name: seriesName || '',
                  }
                : undefined
            }
            defaultTopic={
              topicId
                ? {
                    id: topicId,
                    name: topicName || '',
                  }
                : undefined
            }
            onPublish={publishSuccess}
            defaultTab={operation}
          />
        ) : null}
      </Dialog>
      <style jsx>
        {`
          .publish {
            border: 1px solid #f2f4fa;
          }
          .publish:hover {
            border-color: #f4b700;
            color: #f4b700;
          }
        `}
      </style>
    </>
  )
}

const PublishDropdown: React.FC<IProps> = ({
  hasScore = true,
  seriesId,
  seriesName,
  topicId,
  topicName,
  onClick,
}) => {
  const {
    isShowPublish,
    setIsShowPublish,
    operation,
    handlePublish,
    publishSuccess,
  } = usePublish()
  return (
    <React.Fragment>
      <ul>
        {[...operations, ...(hasScore ? [score] : [])].map((item, index) => {
          return (
            <li
              key={item.title}
              className={classNames(
                'tw-pl-60 tw-pr-16 tw-py-16 tw-cursor-pointer hover:tw-bg-common-yellow hover:tw-bg-opacity-24',
                {
                  'tw-border-t-1 tw-border-solid tw-border-common-cyan-blue':
                    index,
                }
              )}
              onClick={() => {
                if (onClick) onClick(item.title)
                handlePublish(item)
              }}
            >
              <p className="tw-font-medium">{item.title}</p>
              <p className="tw-text-12 tw-text-video-shallow-gray tw-leading-18">
                {item.des}
              </p>
            </li>
          )
        })}
      </ul>
      <Dialog
        className={dialogStyle['space-medium']}
        headerClass={dialogStyle['space-medium']}
        visible={isShowPublish}
        onClose={() => setIsShowPublish(false)}
      >
        {isShowPublish ? (
          <Publisher
            defaultCommunity={
              seriesId
                ? {
                    series_id: seriesId,
                    series_name: seriesName || '',
                  }
                : undefined
            }
            defaultTopic={
              topicId
                ? {
                    id: topicId,
                    name: topicName || '',
                  }
                : undefined
            }
            onPublish={publishSuccess}
            defaultTab={operation}
          />
        ) : null}
      </Dialog>
      <style jsx>
        {`
          li {
            background-size: 36px 36px;
            background-repeat: no-repeat;
            background-position: 16px center;
          }
          li:nth-child(1) {
            background-image: url(${publishVideo});
          }
          li:nth-child(2) {
            background-image: url(${publishPicture});
          }
          li:nth-child(3) {
            background-image: url(${publishQa});
          }
          li:nth-child(4) {
            background-image: url(${publishLong});
          }
        `}
      </style>
    </React.Fragment>
  )
}

export default PublishDropdown
